import { Schedule } from "./types";
import { useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useAppStore } from "./appStore";
const WEEK_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
import { Form, Button, Card, Container, Row, Col, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { SignedOut } from "@clerk/clerk-react";

const timezones = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
];

function generateHalfHourTimes() {
  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const min = m.toString().padStart(2, '0');
      times.push(`${hour}:${min}`);
    }
  }
  return times;
}
const halfHourTimes = generateHalfHourTimes();

function isValidUSPhone(phone: string) {
  return /^\d{10}$/.test(phone);
}

const monthDays = Array.from({ length: 31 }, (_, i) => i + 1);

export function ScheduleForm() {
  // Local state for form fields
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");

  const [onetimeDate, setOnetimeDate] = useState(() => {
    const now = new Date();
    return now.toISOString().slice(0, 10);
  });
  function getRoundedHalfHourTime() {
    const now = new Date();
    let minutes = now.getMinutes();
    let roundedMinutes = minutes < 30 ? 0 : 30;
    const hour = now.getHours().toString().padStart(2, '0');
    const min = roundedMinutes.toString().padStart(2, '0');
    return `${hour}:${min}`;
  }

  const [time, setTime] = useState(getRoundedHalfHourTime);
  const [timezone, setTimezone] = useState("America/New_York");
  const [recurring, setRecurring] = useState("onetime");
  const [weeklyDays, setWeeklyDays] = useState<string[]>([]);

  const appStore = useAppStore();
  const { user } = useUser();
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [monthlyDay, setMonthlyDay] = useState(1);

  const showTimeFields = recurring === 'onetime' || recurring === 'daily' || recurring === 'weekly' || recurring === 'yearly' || recurring === 'monthly';
  const showDateForYearly = recurring === 'yearly';
  const showMonthlyFields = recurring === 'monthly';
  const showDateForOnetime = recurring === 'onetime';

  const handleWeeklyDayChange = (day: string) => {
    if (weeklyDays.includes(day)) {
      setWeeklyDays(weeklyDays.filter(d => d !== day));
    } else {
      setWeeklyDays([...weeklyDays, day]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneTouched(true);
    if (!isValidUSPhone(phoneNumber)) {
      setStatus({ type: "error", message: "Please enter a valid 10-digit US phone number" });
      return;
    }
    if (!message.trim()) {
      setStatus({ type: "error", message: "Message cannot be empty" });
      return;
    }
    if (recurring === 'weekly' && weeklyDays.length === 0) {
      setStatus({ type: "error", message: "For weekly schedules, you must select at least one day." });
      return;
    }
    setLoading(true);
    setStatus(null);

    try {
      let date = onetimeDate; 
      if (recurring === 'onetime') {
        date = onetimeDate;
      } else if (recurring === 'yearly') {
        date = onetimeDate; 
      } else if (recurring === 'monthly') {
        date = `${monthlyDay}`;
      } else if (recurring === 'weekly') {
        date = weeklyDays.join(',');
      }

      const payload: any = {
        userId: user?.id || "anonymous",
        phoneNumber: `+1${phoneNumber}`,
        message,
        frequency: recurring,
        date,
        time,
        timezone,
      };
      const data: { success: boolean; schedule?: Schedule; error?: string } = await appStore.createSchedule(payload);
      if (data.success) {
        setStatus({ type: "success", message: "Schedule created successfully!" });
        // reset();
      } else {
        const errorMsg = typeof data.error === 'string' && data.error.trim() ? data.error : "Failed to create schedule";
        setStatus({ type: "error", message: errorMsg });
      }
    } catch (error) {
      setStatus({ type: "error", message: "Network error. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const phoneInvalid = phoneTouched && !isValidUSPhone(phoneNumber);

  return (
    <Container className="mt-5">
      <SignedOut>
        <Alert variant="danger" className="text-center mb-4">
          Please sign in to schedule a message
        </Alert>
      </SignedOut>
      <Row className="justify-content-center">
        <Col md={7} lg={6}>
          <Card className="shadow-sm border-0">
            <Card.Body className="p-4">
              <Card.Title as="h2" className="mb-4 text-center fw-bold text-primary">TextMeLater - Schedule a Message</Card.Title>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="phoneNumber">
                  <Form.Label className="fw-semibold">Phone Number</Form.Label>
                  <InputGroup size="lg">
                    <InputGroup.Text>+1</InputGroup.Text>
                    <Form.Control
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      onBlur={() => setPhoneTouched(true)}
                      placeholder="XXXXXXXXXX"
                      required
                      maxLength={10}
                      pattern="\d{10}"
                      isInvalid={phoneInvalid}
                    />
                  </InputGroup>
                  <Form.Text>US numbers only. Enter 10 digits after +1.</Form.Text>
                  {phoneInvalid && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      Please enter a valid 10-digit US phone number
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3" controlId="message">
                  <Form.Label className="fw-semibold">Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, 100))}
                    placeholder="Enter your message here..."
                    required
                    minLength={1}
                    maxLength={100}
                    isInvalid={message.trim().length === 0}
                  />
                  <div className="form-text text-end">
                    {message.length}/100 characters
                  </div>
                  {message.trim().length === 0 && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      Message cannot be empty
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3" controlId="recurring">
                  <Form.Label className="fw-semibold">Recurring</Form.Label>
                  <Form.Select
                    size="lg"
                    value={recurring}
                    onChange={(e) => setRecurring(e.target.value)}
                  >
                    <option value="onetime">One Time</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </Form.Select>
                </Form.Group>

                {recurring === 'weekly' && (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Select Days</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {WEEK_DAYS.map(day => (
                        <Form.Check
                          inline
                          key={day}
                          type="checkbox"
                          id={`weekly-day-${day}`}
                          label={day}
                          checked={weeklyDays.includes(day)}
                          onChange={() => handleWeeklyDayChange(day)}
                        />
                      ))}
                    </div>
                    <Form.Text>Choose one or more days for weekly recurrence.</Form.Text>
                  </Form.Group>
                )}

                {showMonthlyFields && (
                  <Form.Group className="mb-3" controlId="monthly-day">
                    <Form.Label className="fw-semibold">Day of Month</Form.Label>
                    <Form.Select
                      size="lg"
                      value={monthlyDay}
                      onChange={e => setMonthlyDay(Number(e.target.value))}
                      required
                    >
                      {monthDays.map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </Form.Select>
                    <Form.Text>Select the day of the month (1-31)</Form.Text>
                  </Form.Group>
                )}

                {showTimeFields && (
                  <>
                    {showDateForOnetime && (
                      <Form.Group className="mb-3" controlId="onetime-date">
                        <Form.Label className="fw-semibold">Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={onetimeDate}
                          onChange={(e) => setOnetimeDate(e.target.value)}
                          required
                        />
                        <Form.Text>Pick the date for your one-time message.</Form.Text>
                      </Form.Group>
                    )}
                    {showDateForYearly && (
                      <Form.Group className="mb-3" controlId="yearly-date">
                        <Form.Label className="fw-semibold">Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={onetimeDate}
                          onChange={(e) => setOnetimeDate(e.target.value)}
                          required
                        />
                        <Form.Text>Pick the date for yearly recurrence.</Form.Text>
                      </Form.Group>
                    )}
                    <Form.Group className="mb-3" controlId="time">
                      <Form.Label className="fw-semibold">Time</Form.Label>
                      <Form.Select
                        size="lg"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        required
                      >
                        {halfHourTimes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </Form.Select>
                      <Form.Text>Select a time (30-minute intervals)</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="timezone">
                      <Form.Label className="fw-semibold">Timezone</Form.Label>
                      <Form.Select
                        size="lg"
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                        required
                      >
                        {timezones.map((tz) => (
                          <option key={tz} value={tz}>{tz}</option>
                        ))}
                        {!timezones.includes(timezone) && (
                          <option value={timezone}>{timezone}</option>
                        )}
                      </Form.Select>
                      <Form.Text>Default: EST (America/New_York)</Form.Text>
                    </Form.Group>
                  </>
                )}

                {status && (
                  <Alert variant={status.type === "success" ? "success" : "danger"} className="mt-3">
                    {status.message}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-100 mt-3 shadow-sm"
                  disabled={loading || !user}
                >
                  {loading ? (
                    <span>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                      Scheduling...
                    </span>
                  ) : (
                    "Schedule Message"
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
          <div className="text-center mt-4 text-muted small">
            <span>Powered by Bun + React + Zustand + Bootstrap</span>
          </div>
        </Col>
      </Row>
    </Container>
  );
}
